import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDeleteModal } from "../../../../hooks/shared/useDeleteModal";
import { filterRowsByQuery } from "../../../../utils/shared/searchHelpers";
import { formatValidationErrors } from "../../utils/adminApiHelpers";
import {
  buildPartnerFormData,
  buildPartnerFormDataForUpdate,
  createPartner,
  deletePartner,
  fetchPartnerById,
  fetchPartnerList,
  unwrapList,
  unwrapOne,
  updatePartner,
} from "../api";
import { EMPTY_PARTNER_FORM } from "../constants/emptyDefaults";
import { getPartnerColumns } from "../constants/partnerColumns";
import { getPartnerFields } from "../constants/partnerFields";
import { normalizePartnerItem } from "../utils/normalizePartner";

export function useManagePartners() {
  const { t } = useTranslation("dashboard");
  const tk = useCallback((key) => t(`managePartners.${key}`), [t]);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const deleteModal = useDeleteModal();
  const [editingItem, setEditingItem] = useState(null);
  const [newFormKey, setNewFormKey] = useState(0);
  const [submitError, setSubmitError] = useState(null);

  const editingId = editingItem?.id;

  const { data: editingDetail, isFetching: isEditLoading } = useQuery({
    queryKey: ["admin", "partners", "detail", editingId],
    queryFn: async () => {
      const res = await fetchPartnerById(editingId);
      const raw = unwrapOne(res);
      return normalizePartnerItem(raw) ?? normalizePartnerItem(raw?.data);
    },
    enabled: editingId != null,
  });

  const formDefaults = useMemo(() => {
    if (!editingItem) return { ...EMPTY_PARTNER_FORM };
    const s = editingDetail ?? editingItem;
    return {
      name_ar: s.name_ar ?? "",
      name_en: s.name_en ?? "",
    };
  }, [editingItem, editingDetail]);

  const fields = useMemo(() => getPartnerFields(tk), [tk]);
  const columns = useMemo(() => getPartnerColumns(tk), [tk]);

  const {
    data: rows = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["admin", "partners"],
    queryFn: async () => {
      const res = await fetchPartnerList();
      return unwrapList(res).map(normalizePartnerItem).filter(Boolean);
    },
  });

  const createMutation = useMutation({
    mutationFn: (fd) => createPartner(fd),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "partners"] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, formData }) => updatePartner(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "partners"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deletePartner(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "partners"] });
    },
  });

  const handleSubmit = async (data) => {
    setSubmitError(null);
    const fd = editingItem
      ? buildPartnerFormDataForUpdate(data)
      : buildPartnerFormData(data);
    try {
      if (editingItem) {
        await updateMutation.mutateAsync({ id: editingItem.id, formData: fd });
        setEditingItem(null);
      } else {
        await createMutation.mutateAsync(fd);
      }
      setNewFormKey((k) => k + 1);
    } catch (err) {
      setSubmitError(formatValidationErrors(err));
    }
  };

  const confirmDelete = async () => {
    if (deleteModal.deleteId == null) return;
    try {
      await deleteMutation.mutateAsync(deleteModal.deleteId);
    } finally {
      deleteModal.close();
    }
  };

  const filtered = useMemo(
    () => filterRowsByQuery(rows, search, ["name_en", "name_ar"]),
    [rows, search]
  );

  const errorMessage =
    error?.response?.data?.message ||
    error?.message ||
    (isError ? tk("alerts.loadFailed") : null);

  const pending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return {
    tk,
    search,
    setSearch,
    deleteModal,
    editingItem,
    newFormKey,
    submitError,
    isEditLoading,
    formDefaults,
    fields,
    columns,
    filtered,
    isLoading,
    errorMessage,
    pending,
    handleSubmit,
    confirmDelete,
    handleEdit: setEditingItem,
    handleDeleteClick: deleteModal.open,
    cancelEdit: () => setEditingItem(null),
  };
}
